import { X } from 'lucide-react'
import { Checkbox as CheckboxPrimitive, RadioGroup as RadioGroupPrimitive } from 'radix-ui'
import type { ButtonHTMLAttributes, ComponentProps, ReactNode } from 'react'
import { cn } from '../lib/cn'

/**
 * Escolha — componente 04: única, múltipla e etiqueta.
 * Quadrado de 20px com fio; selecionado é preenchimento ink sólido, sem ícone.
 * Radio e checkbox nativos são proibidos (trazem o azul do sistema operacional).
 * A linha inteira é o alvo (`ChoiceRow`). Acima de 12 opções: busca em foco e as escolhas viram `Chip`.
 */

const boxClassName =
  'size-5 shrink-0 cursor-pointer border border-hairline-heavy bg-surface transition-[background-color,border-color] hover:border-ink data-[state=checked]:border-ink data-[state=checked]:bg-ink disabled:cursor-default disabled:border-hairline disabled:bg-pampas'

export type CheckboxProps = ComponentProps<typeof CheckboxPrimitive.Root>

export function Checkbox({ className, ...props }: CheckboxProps) {
  return (
    <CheckboxPrimitive.Root data-slot="checkbox" className={cn(boxClassName, className)} {...props}>
      <CheckboxPrimitive.Indicator className="block size-full" />
    </CheckboxPrimitive.Root>
  )
}

export type RadioGroupProps = ComponentProps<typeof RadioGroupPrimitive.Root>

export function RadioGroup({ className, ...props }: RadioGroupProps) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={cn('grid gap-2', className)}
      {...props}
    />
  )
}

export type RadioProps = ComponentProps<typeof RadioGroupPrimitive.Item>

export function Radio({ className, ...props }: RadioProps) {
  return (
    <RadioGroupPrimitive.Item data-slot="radio" className={cn(boxClassName, className)} {...props}>
      <RadioGroupPrimitive.Indicator className="block size-full" />
    </RadioGroupPrimitive.Item>
  )
}

export interface ChoiceRowProps {
  /** O controle: `<Checkbox>` ou `<Radio>`. */
  control: ReactNode
  children: ReactNode
  /** Texto secundário abaixo do rótulo. */
  description?: ReactNode
  /** Conteúdo à direita (contador, etiqueta). */
  trailing?: ReactNode
  /** Sem borda própria — para listas onde o fio vem da linha. */
  bare?: boolean
  className?: string
  htmlFor?: string
}

/**
 * Linha inteira clicável, alvo mínimo de 48px. Selecionada ganha fio ink e fundo pampas
 * (via `:has()` no estado do controle).
 */
export function ChoiceRow({
  control,
  children,
  description,
  trailing,
  bare,
  className,
  htmlFor,
}: ChoiceRowProps) {
  return (
    <label
      htmlFor={htmlFor}
      data-slot="choice-row"
      className={cn(
        // `relative`: o input nativo escondido do Radix (absoluto) fica preso à linha. Sem isso, numa
        // lista com rolagem própria (PickList) ele escapa do recorte e estica a página inteira.
        'min-h-touch relative flex cursor-pointer items-center gap-3 px-4 py-[14px] transition-[border-color,background-color]',
        bare
          ? 'border-hairline hover:bg-pampas border-b last:border-b-0'
          : 'border-hairline-strong bg-surface hover:border-hairline-heavy has-[[data-state=checked]]:border-ink has-[[data-state=checked]]:bg-pampas border',
        className,
      )}
    >
      {control}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="font-body text-lead text-ink leading-[1.3] font-light">{children}</span>
        {description ? (
          <span className="font-body text-eyebrow text-secondary mt-1 leading-[1.5] font-light tracking-normal">
            {description}
          </span>
        ) : null}
      </span>
      {trailing}
    </label>
  )
}

export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  /** Quando presente, o chip ganha o ✕ e é removível. */
  onRemove?: () => void
  removeLabel?: string
}

/** Escolha feita, mostrada acima da lista como etiqueta removível ("Mento ✕"). */
export function Chip({
  className,
  children,
  onRemove,
  removeLabel = 'Remover',
  ...props
}: ChipProps) {
  if (!onRemove) {
    return (
      <span
        data-slot="chip"
        className={cn(
          'bg-ink font-body text-pampas inline-flex items-center gap-2 px-3 py-2 text-[12px] leading-none font-light',
          className,
        )}
      >
        {children}
      </span>
    )
  }
  return (
    <button
      type="button"
      data-slot="chip"
      onClick={onRemove}
      aria-label={`${removeLabel}: ${typeof children === 'string' ? children : ''}`.trim()}
      className={cn(
        'bg-ink font-body text-pampas inline-flex cursor-pointer items-center gap-2 px-3 py-2 text-[12px] leading-none font-light hover:opacity-[.88]',
        className,
      )}
      {...props}
    >
      {children}
      <X aria-hidden strokeWidth={1.6} className="size-3" />
    </button>
  )
}

/** "+ Adicionar" ao lado dos chips. */
export function ChipAdd({
  className,
  children = '+ Adicionar',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      data-slot="chip-add"
      className={cn(
        'border-hairline-heavy font-body text-ink hover:border-ink disabled:border-hairline disabled:text-label disabled:hover:border-hairline inline-flex cursor-pointer items-center border px-3 py-2 text-[12px] leading-none font-light disabled:cursor-default',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
}

export interface ChipToggleProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  pressed: boolean
  onPressedChange: (pressed: boolean) => void
}

/**
 * Escolha múltipla curta, em linha ("PIX · Débito · Crédito · Dinheiro"). Ligado: ink sólido;
 * desligado: contorno. Para listas longas, use `PickList`.
 */
export function ChipToggle({
  pressed,
  onPressedChange,
  className,
  children,
  ...props
}: ChipToggleProps) {
  return (
    <button
      type="button"
      data-slot="chip-toggle"
      aria-pressed={pressed}
      onClick={() => onPressedChange(!pressed)}
      className={cn(
        'min-h-touch-desk max-desk:min-h-touch font-body inline-flex cursor-pointer items-center px-4 text-[13px] leading-none font-light transition-[border-color,background-color]',
        pressed
          ? 'bg-ink text-pampas'
          : 'border-hairline-heavy text-ink hover:border-ink bg-surface border',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
}
